import { SubscriptionDocWithId } from "../../app/Subscriptions/types/SubscriptionType.ts";
import { createGenericSlice, GenericState } from "../GenericSlice.ts";

import { PayloadAction } from "@reduxjs/toolkit";
import { db } from '../../Config/firebase';
import { doc, updateDoc, Timestamp } from 'firebase/firestore';
// Keep the import for type definition, but we're not using the reducers anymore


// New syllabus structure types
export type TimeLog = {
    id: string;
    timestamp: number;
    duration: number; // in minutes
    notes?: string;
};

export type Subtopic = {
    id: string;
    name: string;
    description?: string;
    order: number;
    status?: 'not_started' | 'in_progress' | 'completed';
    updatedAt?: Timestamp;
};

export type Topic = {
    id: string;
    name: string;
    description?: string;
    order: number;
    subtopics: Subtopic[];
    status?: 'not_started' | 'in_progress' | 'completed';
    scheduledDuration?: number; // Duration in minutes
    updatedAt?: Timestamp;
};

export type Session = {
    id: string;
    name: string;
    description?: string;
    order: number;
    topics: Topic[];
    status?: 'not_started' | 'in_progress' | 'completed';
};

export type Level = {
    id: string;
    name: string;
    description?: string;
    order: number;
    sessions: Session[];
    status?: 'not_started' | 'in_progress' | 'completed';
};

export type Syllabus = {
    id: string;
    name: string;
    description?: string;
    levels: Level[];
    createdAt: number;
    lastModified: number;
};

// Keep ZoomMeeting type for backward compatibility
export type ZoomMeeting = {
    id: string;
    meetingId: string;
    password: string;
    topic: string;
    startTime: string;
    duration: number;
    joinUrl?: string;
    createdAt: number;
};

type State = {
    data: SubscriptionDocWithId[]
}

const initialState: State = {
    data: []
}


const subscriptionTableSlice = createGenericSlice({
    name: "subscriptionTable",
    initialState: initialState as GenericState<SubscriptionDocWithId[]>,
    reducers: {
        success: {
            reducer: (state, action: PayloadAction<SubscriptionDocWithId[]>) => {
                
                state.data = action.payload;
                state.status = 'finished'
            },
            prepare: ({ data: Subscription, role }) => {
                Subscription = Subscription || [];

                const subscriptionArray: SubscriptionDocWithId[] = Subscription.map((data: any) => {
                    return {
                        id: data.id,
                        BillingType: data.BillingType,
                        CreatedAt: data.CreatedAt.toDate().toISOString(),
                        Description: data.Description,
                        ModifiedAt: data.ModifiedAt.toDate().toISOString(),
                        Name: data.Name,
                        PaymentCycle: data.PaymentCycle,
                        Price: data.Price,
                        geoLocation: {
                            hash: data.geoLocation?.hash,
                        },
                        placeAddress: data.placeAddress,
                        placeName: data.placeName,
                        max_pause_days: data.max_pause_days,
                        paymentProfileId: data.paymentProfileId,
                        picture: data.picture,
                        showMeInList: data.showMeInList,
                        type: data.type,
                        advance: data.advance ? data.advance : 0,
                        activeSubscribersCount: data.activeSubscribersCount,
                        groupLink: data.groupLink,
                        cyclePriceArray: data.cyclePriceArray ? data.cyclePriceArray : [],
                        days: data.days ? data.days : [1, 1, 1, 1, 1, 1, 1],
                        align2monthlyBoundary: data.align2monthlyBoundary ? data.align2monthlyBoundary : false,
                        subscriptionType: data.subscriptionType,
                        isCountHourly: data.isCountHourly ? data.isCountHourly : false,
                        NoGSTBatch: data.NoGSTBatch ? data.NoGSTBatch : false,
                        syllabus: data.syllabus,
                        assignedSyllabus: data.assignedSyllabus || [],
                        levels: data.levels || []
                    }
                })

                const assignedSubscriptions = role?.assignedSubscriptions;
                const activeRole = role?.role;
                console.log(assignedSubscriptions,activeRole,"hello")
                if (assignedSubscriptions && (activeRole === "Manager" || activeRole === "Observer")) {
                    const filteredSubscriptions = subscriptionArray.filter(subscription => assignedSubscriptions.includes(subscription.id));
                    return { payload: filteredSubscriptions }
                }
                return { payload: subscriptionArray }
            }
        },
        updateAssignedSyllabus: (state, action: PayloadAction<{ 
            serviceProviderId: string;
            subscriptionId: string; 
            assignedSyllabus: any[] 
        }>) => {
            const subscription = state.data?.find(sub => sub.id === action.payload.subscriptionId);
            if (subscription) {
                subscription.assignedSyllabus = action.payload.assignedSyllabus;
                
                const docRef = doc(
                    db, 
                    'ServiceProviders', 
                    action.payload.serviceProviderId,
                    'Subscriptions', 
                    action.payload.subscriptionId
                );
                updateDoc(docRef, {
                    assignedSyllabus: subscription.assignedSyllabus
                });
            }
        },
        // Syllabus reducers
        createSyllabus: (state, action: PayloadAction<{ 
            serviceProviderId: string;
            subscriptionId: string; 
            syllabus: Syllabus 
        }>) => {
            const subscription = state.data?.find(sub => sub.id === action.payload.subscriptionId);
            if (subscription) {
                subscription.syllabus = action.payload.syllabus;
                
                const docRef = doc(
                    db, 
                    'ServiceProviders', 
                    action.payload.serviceProviderId,
                    'Subscriptions', 
                    action.payload.subscriptionId
                );
                updateDoc(docRef, {
                    syllabus: subscription.syllabus
                });
            }
        },
        
        updateSyllabus: (state, action: PayloadAction<{ 
            serviceProviderId: string;
            subscriptionId: string; 
            updates: Partial<Syllabus> 
        }>) => {
            const subscription = state.data?.find(sub => sub.id === action.payload.subscriptionId);
            if (subscription && subscription.syllabus) {
                subscription.syllabus = {
                    ...subscription.syllabus,
                    ...action.payload.updates,
                    lastModified: Date.now()
                };
                
                const docRef = doc(
                    db, 
                    'ServiceProviders', 
                    action.payload.serviceProviderId,
                    'Subscriptions', 
                    action.payload.subscriptionId
                );
                updateDoc(docRef, {
                    syllabus: subscription.syllabus
                });
            }
        },
        
        addLevel: (state, action: PayloadAction<{ 
            serviceProviderId: string;
            subscriptionId: string; 
            level: Level 
        }>) => {
            const subscription = state.data?.find(sub => sub.id === action.payload.subscriptionId);
            if (subscription && subscription.syllabus) {
                if (!subscription.syllabus.levels) {
                    subscription.syllabus.levels = [];
                }
                subscription.syllabus.levels.push(action.payload.level);
                subscription.syllabus.lastModified = Date.now();
                
                const docRef = doc(
                    db, 
                    'ServiceProviders', 
                    action.payload.serviceProviderId,
                    'Subscriptions', 
                    action.payload.subscriptionId
                );
                updateDoc(docRef, {
                    syllabus: subscription.syllabus
                });
            }
        },
        
        updateLevel: (state, action: PayloadAction<{ 
            serviceProviderId: string;
            subscriptionId: string; 
            levelId: string;
            updates: Partial<Level> 
        }>) => {
            const subscription = state.data?.find(sub => sub.id === action.payload.subscriptionId);
            if (subscription && subscription.syllabus && subscription.syllabus.levels) {
                const levelIndex = subscription.syllabus.levels.findIndex(
                    level => level.id === action.payload.levelId
                );
                
                if (levelIndex >= 0) {
                    subscription.syllabus.levels[levelIndex] = {
                        ...subscription.syllabus.levels[levelIndex],
                        ...action.payload.updates
                    };
                    subscription.syllabus.lastModified = Date.now();
                    
                    const docRef = doc(
                        db, 
                        'ServiceProviders', 
                        action.payload.serviceProviderId,
                        'Subscriptions', 
                        action.payload.subscriptionId
                    );
                    updateDoc(docRef, {
                        syllabus: subscription.syllabus
                    });
                }
            }
        },
        
        deleteLevel: (state, action: PayloadAction<{ 
            serviceProviderId: string;
            subscriptionId: string; 
            levelId: string 
        }>) => {
            const subscription = state.data?.find(sub => sub.id === action.payload.subscriptionId);
            if (subscription && subscription.syllabus && subscription.syllabus.levels) {
                subscription.syllabus.levels = subscription.syllabus.levels.filter(
                    level => level.id !== action.payload.levelId
                );
                subscription.syllabus.lastModified = Date.now();
                
                const docRef = doc(
                    db, 
                    'ServiceProviders', 
                    action.payload.serviceProviderId,
                    'Subscriptions', 
                    action.payload.subscriptionId
                );
                updateDoc(docRef, {
                    syllabus: subscription.syllabus
                });
            }
        },
        
        addSession: (state, action: PayloadAction<{ 
            serviceProviderId: string;
            subscriptionId: string; 
            levelId: string;
            session: Session 
        }>) => {
            const subscription = state.data?.find(sub => sub.id === action.payload.subscriptionId);
            if (subscription && subscription.syllabus && subscription.syllabus.levels) {
                const level = subscription.syllabus.levels.find(l => l.id === action.payload.levelId);
                if (level) {
                    if (!level.sessions) {
                        level.sessions = [];
                    }
                    level.sessions.push(action.payload.session);
                    subscription.syllabus.lastModified = Date.now();
                    
                    const docRef = doc(
                        db, 
                        'ServiceProviders', 
                        action.payload.serviceProviderId,
                        'Subscriptions', 
                        action.payload.subscriptionId
                    );
                    updateDoc(docRef, {
                        syllabus: subscription.syllabus
                    });
                }
            }
        },
        
        updateSession: (state, action: PayloadAction<{ 
            serviceProviderId: string;
            subscriptionId: string; 
            levelId: string;
            sessionId: string;
            updates: Partial<Session> 
        }>) => {
            const subscription = state.data?.find(sub => sub.id === action.payload.subscriptionId);
            if (subscription && subscription.syllabus && subscription.syllabus.levels) {
                const level = subscription.syllabus.levels.find(l => l.id === action.payload.levelId);
                if (level && level.sessions) {
                    const sessionIndex = level.sessions.findIndex(s => s.id === action.payload.sessionId);
                    if (sessionIndex >= 0) {
                        level.sessions[sessionIndex] = {
                            ...level.sessions[sessionIndex],
                            ...action.payload.updates
                        };
                        subscription.syllabus.lastModified = Date.now();
                        
                        const docRef = doc(
                            db, 
                            'ServiceProviders', 
                            action.payload.serviceProviderId,
                            'Subscriptions', 
                            action.payload.subscriptionId
                        );
                        updateDoc(docRef, {
                            syllabus: subscription.syllabus
                        });
                    }
                }
            }
        },
        
        deleteSession: (state, action: PayloadAction<{ 
            serviceProviderId: string;
            subscriptionId: string; 
            levelId: string;
            sessionId: string
        }>) => {
            const subscription = state.data?.find(sub => sub.id === action.payload.subscriptionId);
            if (subscription && subscription.syllabus && subscription.syllabus.levels) {
                const level = subscription.syllabus.levels.find(l => l.id === action.payload.levelId);
                if (level && level.sessions) {
                    level.sessions = level.sessions.filter(s => s.id !== action.payload.sessionId);
                    subscription.syllabus.lastModified = Date.now();
                    
                    const docRef = doc(
                        db, 
                        'ServiceProviders', 
                        action.payload.serviceProviderId,
                        'Subscriptions', 
                        action.payload.subscriptionId
                    );
                    updateDoc(docRef, {
                        syllabus: subscription.syllabus
                    });
                }
            }
        },
        
        addTopic: (state, action: PayloadAction<{ 
            serviceProviderId: string;
            subscriptionId: string; 
            levelId: string;
            sessionId: string;
            topic: Topic 
        }>) => {
            const subscription = state.data?.find(sub => sub.id === action.payload.subscriptionId);
            if (subscription && subscription.syllabus && subscription.syllabus.levels) {
                const level = subscription.syllabus.levels.find(l => l.id === action.payload.levelId);
                if (level && level.sessions) {
                    const session = level.sessions.find(s => s.id === action.payload.sessionId);
                    if (session) {
                        if (!session.topics) {
                            session.topics = [];
                        }
                        session.topics.push(action.payload.topic);
                        subscription.syllabus.lastModified = Date.now();
                        
                        const docRef = doc(
                            db, 
                            'ServiceProviders', 
                            action.payload.serviceProviderId,
                            'Subscriptions', 
                            action.payload.subscriptionId
                        );
                        updateDoc(docRef, {
                            syllabus: subscription.syllabus
                        });
                    }
                }
            }
        },
        
        updateTopic: (state, action: PayloadAction<{ 
            serviceProviderId: string;
            subscriptionId: string; 
            levelId: string;
            sessionId: string;
            topicId: string;
            updates: Partial<Topic> 
        }>) => {
            const subscription = state.data?.find(sub => sub.id === action.payload.subscriptionId);
            if (subscription && subscription.syllabus && subscription.syllabus.levels) {
                const level = subscription.syllabus.levels.find(l => l.id === action.payload.levelId);
                if (level && level.sessions) {
                    const session = level.sessions.find(s => s.id === action.payload.sessionId);
                    if (session && session.topics) {
                        const topicIndex = session.topics.findIndex(t => t.id === action.payload.topicId);
                        if (topicIndex >= 0) {
                            session.topics[topicIndex] = {
                                ...session.topics[topicIndex],
                                ...action.payload.updates
                            };
                            subscription.syllabus.lastModified = Date.now();
                            
                            const docRef = doc(
                                db, 
                                'ServiceProviders', 
                                action.payload.serviceProviderId,
                                'Subscriptions', 
                                action.payload.subscriptionId
                            );
                            updateDoc(docRef, {
                                syllabus: subscription.syllabus
                            });
                        }
                    }
                }
            }
        },
        
        deleteTopic: (state, action: PayloadAction<{ 
            serviceProviderId: string;
            subscriptionId: string; 
            levelId: string;
            sessionId: string;
            topicId: string
        }>) => {
            const subscription = state.data?.find(sub => sub.id === action.payload.subscriptionId);
            if (subscription && subscription.syllabus && subscription.syllabus.levels) {
                const level = subscription.syllabus.levels.find(l => l.id === action.payload.levelId);
                if (level && level.sessions) {
                    const session = level.sessions.find(s => s.id === action.payload.sessionId);
                    if (session && session.topics) {
                        session.topics = session.topics.filter(t => t.id !== action.payload.topicId);
                        subscription.syllabus.lastModified = Date.now();
                        
                        const docRef = doc(
                            db, 
                            'ServiceProviders', 
                            action.payload.serviceProviderId,
                            'Subscriptions', 
                            action.payload.subscriptionId
                        );
                        updateDoc(docRef, {
                            syllabus: subscription.syllabus
                        });
                    }
                }
            }
        },
        
        addSubtopic: (state, action: PayloadAction<{ 
            serviceProviderId: string;
            subscriptionId: string; 
            levelId: string;
            sessionId: string;
            topicId: string;
            subtopic: Subtopic 
        }>) => {
            const subscription = state.data?.find(sub => sub.id === action.payload.subscriptionId);
            if (subscription && subscription.syllabus && subscription.syllabus.levels) {
                const level = subscription.syllabus.levels.find(l => l.id === action.payload.levelId);
                if (level && level.sessions) {
                    const session = level.sessions.find(s => s.id === action.payload.sessionId);
                    if (session && session.topics) {
                        const topic = session.topics.find(t => t.id === action.payload.topicId);
                        if (topic) {
                            if (!topic.subtopics) {
                                topic.subtopics = [];
                            }
                            topic.subtopics.push(action.payload.subtopic);
                            subscription.syllabus.lastModified = Date.now();
                            
                            const docRef = doc(
                                db, 
                                'ServiceProviders', 
                                action.payload.serviceProviderId,
                                'Subscriptions', 
                                action.payload.subscriptionId
                            );
                            updateDoc(docRef, {
                                syllabus: subscription.syllabus
                            });
                        }
                    }
                }
            }
        },
        
        updateSubtopic: (state, action: PayloadAction<{ 
            serviceProviderId: string;
            subscriptionId: string; 
            levelId: string;
            sessionId: string;
            topicId: string;
            subtopicId: string;
            updates: Partial<Subtopic> 
        }>) => {
            const subscription = state.data?.find(sub => sub.id === action.payload.subscriptionId);
            if (subscription && subscription.syllabus && subscription.syllabus.levels) {
                const level = subscription.syllabus.levels.find(l => l.id === action.payload.levelId);
                if (level && level.sessions) {
                    const session = level.sessions.find(s => s.id === action.payload.sessionId);
                    if (session && session.topics) {
                        const topic = session.topics.find(t => t.id === action.payload.topicId);
                        if (topic && topic.subtopics) {
                            const subtopicIndex = topic.subtopics.findIndex(st => st.id === action.payload.subtopicId);
                            if (subtopicIndex >= 0) {
                                topic.subtopics[subtopicIndex] = {
                                    ...topic.subtopics[subtopicIndex],
                                    ...action.payload.updates
                                };
                                subscription.syllabus.lastModified = Date.now();
                                
                                const docRef = doc(
                                    db, 
                                    'ServiceProviders', 
                                    action.payload.serviceProviderId,
                                    'Subscriptions', 
                                    action.payload.subscriptionId
                                );
                                updateDoc(docRef, {
                                    syllabus: subscription.syllabus
                                });
                            }
                        }
                    }
                }
            }
        },
        
        deleteSubtopic: (state, action: PayloadAction<{ 
            serviceProviderId: string;
            subscriptionId: string; 
            levelId: string;
            sessionId: string;
            topicId: string;
            subtopicId: string
        }>) => {
            const subscription = state.data?.find(sub => sub.id === action.payload.subscriptionId);
            if (subscription && subscription.syllabus && subscription.syllabus.levels) {
                const level = subscription.syllabus.levels.find(l => l.id === action.payload.levelId);
                if (level && level.sessions) {
                    const session = level.sessions.find(s => s.id === action.payload.sessionId);
                    if (session && session.topics) {
                        const topic = session.topics.find(t => t.id === action.payload.topicId);
                        if (topic && topic.subtopics) {
                            topic.subtopics = topic.subtopics.filter(st => st.id !== action.payload.subtopicId);
                            subscription.syllabus.lastModified = Date.now();
                            
                            const docRef = doc(
                                db, 
                                'ServiceProviders', 
                                action.payload.serviceProviderId,
                                'Subscriptions', 
                                action.payload.subscriptionId
                            );
                            updateDoc(docRef, {
                                syllabus: subscription.syllabus
                            });
                        }
                    }
                }
            }
        }
    }
})

// Export specific actions individually
export const {
    success,
    // Export new syllabus actions
    createSyllabus,
    updateSyllabus,
    addLevel,
    updateLevel,
    deleteLevel,
    addSession,
    updateSession,
    deleteSession,
    addTopic,
    updateTopic,
    deleteTopic,
    addSubtopic,
    updateSubtopic,
    deleteSubtopic,
    updateAssignedSyllabus
} = subscriptionTableSlice.actions;

// Export generic actions type
export const genericActions = subscriptionTableSlice.actions;

// Export reducer
export default subscriptionTableSlice.reducer